import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import DOMPurify from 'dompurify';
import { Calendar, Clock, User, Hash, ChevronDown, ArrowLeft } from 'lucide-react';
import config from '../config';
import Breadcrumbs from '../components/Breadcrumbs';
import ArticleEnhancements from '../components/ArticleEnhancements';
import ArticleStructuredData from '../components/ArticleStructuredData';
import TableOfContents from '../components/TableOfContents';
import parse from 'html-react-parser';

const isPrerender = () =>
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const ArticlePage = () => {
  const { category, articleSlug } = useParams();
  const [article, setArticle] = useState(null);
  // 'loading' | 'ready' | 'missing' | 'error'
  // 'missing' means the article really is gone and we tell Google so with a
  // noindex. 'error' means we could not load it this time (dead chunk after a
  // deploy, API blip) and the article may well still be fine, so we render the
  // same shell WITHOUT a robots tag rather than deindexing a live page.
  const [status, setStatus] = useState('loading');
  const [tocVisible, setTocVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      setArticle(null);

      // 1) Build-time snapshot. Every published article is committed as
      // src/data/articles/<category>/<slug>.json, so webpack emits one chunk
      // per article and this import resolves same-origin. That makes the
      // full article body render during the react-snap prerender (no CORS,
      // no external network), which is what bakes real content into the
      // static HTML that crawlers and AI bots read.
      try {
        const mod = await import(`../data/articles/${category}/${articleSlug}.json`);
        if (!cancelled) {
          setArticle(mod.default || mod);
          setStatus('ready');
        }
        return;
      } catch (err) {
        // Not in the snapshot; fall through to the runtime API.
      }

      // 2) Never hit the cross-origin API during prerender: it is
      // CORS-blocked from the headless crawler's localhost origin and a
      // hanging request can trip react-snap's navigation timeout.
      if (isPrerender()) {
        if (!cancelled) setStatus('missing');
        return;
      }

      // 3) Runtime fallback for articles published after the snapshot was
      // committed. Real browsers can fetch the API directly.
      try {
        const response = await fetch(`${config.apiUrl}/articles/${category}/${articleSlug}/`);
        if (!response.ok) {
          // A 4xx is the API telling us the article is genuinely gone. A 5xx
          // is the API having a bad minute, which is not grounds for noindex.
          if (!cancelled) {
            setStatus(response.status >= 400 && response.status < 500 ? 'missing' : 'error');
          }
          return;
        }
        const data = await response.json();
        if (!cancelled) {
          setArticle(data);
          setStatus('ready');
        }
      } catch (err) {
        // Network failure, CORS, or a code-split chunk that 404'd because the
        // crawler is rendering stale HTML against a newer deploy. Treat as
        // transient: never emit noindex on it.
        if (!cancelled) setStatus('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [category, articleSlug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const toggleToc = () => {
    setTocVisible(!tocVisible);
  };

  // Function to split content into sections based on <h2> tags
  const splitContentIntoSections = (htmlContent) => {
    const sanitizedContent = DOMPurify.sanitize(htmlContent);
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedContent, 'text/html');
    const body = doc.body;

    const sections = [];
    let currentSection = { title: null, content: [] };

    Array.from(body.children).forEach((node) => {
      if (node.tagName.toLowerCase() === 'h2') {
        // Push the previous section if it exists
        if (currentSection.title) {
          sections.push({ ...currentSection });
          currentSection = { title: null, content: [] };
        }
        currentSection.title = node.textContent;
      } else {
        if (!currentSection.title) {
          // If there's content before the first <h2>, assign it to a default section
          currentSection.title = 'Introduction';
        }
        currentSection.content.push(node.outerHTML);
      }
    });

    // Push the last section
    if (currentSection.title) {
      sections.push({ ...currentSection });
    }

    return sections;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sortio-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'missing' || status === 'error' || !article) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16">
        <Helmet>
          <title>Article Not Found | Sortio</title>
          {status === 'missing' && <meta name="robots" content="noindex" />}
        </Helmet>
        <div className="container mx-auto px-6">
          <div className="apple-panel p-10 max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4 text-apple-50">Article not found</h1>
            <p className="text-apple-300 mb-8">
              This article does not exist or has been moved. Browse the full library or jump to a guide instead.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/articles" className="apple-button inline-flex items-center justify-center px-5 py-2.5">
                <ArrowLeft className="mr-2 w-4 h-4" />
                All Articles
              </Link>
              <Link to="/guides" className="apple-panel-button inline-flex items-center justify-center px-5 py-2.5">
                Browse Guides
              </Link>
              <Link to="/download" className="apple-panel-button inline-flex items-center justify-center px-5 py-2.5">
                Download Sortio
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tableOfContents = article.table_of_contents || [];
  const sections = splitContentIntoSections(article.content || '');

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Background blur orbs */}
      <div className="fixed -bottom-32 -right-32 w-96 h-96 bg-sortio-primary/10 rounded-full blur-3xl opacity-60"></div>
      <div className="fixed top-1/2 -left-32 w-96 h-96 bg-sortio-secondary/10 rounded-full blur-3xl opacity-60"></div>

      <Helmet>
        <title>{`${article.title} - Sortio`}</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://www.getsortio.com/articles/${category}/${articleSlug}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.getsortio.com/articles/${category}/${articleSlug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
      </Helmet>

      <div className="container mx-auto px-6 relative z-10">
        <Breadcrumbs category={article.category || category} articleTitle={article.title} />

        <div className="flex flex-col lg:flex-row lg:gap-8 mt-8">
          {/* Main content */}
          <article className="lg:w-2/3">
            {/* Article header */}
            <div className="apple-panel p-8 mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4 text-apple-50">
                {article.title}
              </h1>

              <div className="flex flex-wrap gap-4 text-sm text-apple-400 mb-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1.5" />
                  <span>{formatDate(article.created_at)}</span>
                </div>

                {article.read_time && (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1.5" />
                    <span>{article.read_time}</span>
                  </div>
                )}

                {article.author && (
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1.5" />
                    <span>{article.author}</span>
                  </div>
                )}
              </div>

              {article.excerpt && (
                <p className="text-apple-300 text-lg leading-relaxed">{article.excerpt}</p>
              )}

              {/* Mobile TOC toggle */}
              {tableOfContents.length > 0 && (
                <button
                  onClick={toggleToc}
                  className="lg:hidden mt-6 w-full flex items-center justify-between p-3 bg-apple-800/50 rounded-lg text-apple-200 hover:bg-apple-700/70 transition-colors"
                >
                  <span className="font-medium">Table of Contents</span>
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${tocVisible ? 'transform rotate-180' : ''}`} />
                </button>
              )}

              {tocVisible && tableOfContents.length > 0 && (
                <div className="lg:hidden mt-3 p-4 bg-apple-800/30 rounded-lg">
                  <TableOfContents tableOfContents={tableOfContents} />
                </div>
              )}
            </div>

            {/* Article content */}
            <div className="space-y-8">
              {article.youtube_video_url && (
                <div className="mb-8">
                  <iframe
                    width="100%"
                    height="315"
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(article.youtube_video_url)}?rel=0`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded-lg shadow-lg"
                  ></iframe>
                </div>
              )}

              <ArticleEnhancements article={article} content={article.content} />

              {sections.map((section, index) => {
                const sectionId = tableOfContents[index]?.id || `section-${index}`;
                return (
                  <section key={index} id={sectionId} className="apple-panel p-8">
                    <h2 className="text-2xl font-bold mb-6 text-apple-50 border-b border-apple-700/50 pb-3">
                      {section.title}
                    </h2>
                    <div className="prose prose-invert max-w-none prose-headings:text-apple-100 prose-p:text-apple-300 prose-strong:text-apple-200 prose-li:text-apple-300 prose-a:text-sortio-primary hover:prose-a:text-sortio-primary/80 prose-a:no-underline hover:prose-a:underline">
                      {parse(section.content.join(' '))}
                    </div>
                  </section>
                );
              })}
            </div>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-8 apple-panel p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-apple-400 flex items-center">
                    <Hash className="w-4 h-4 mr-1" />
                    Tags:
                  </span>
                  {article.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/articles?tag=${tag}`}
                      className="px-3 py-1 bg-apple-800/50 hover:bg-apple-700/70 rounded-full text-sm text-apple-300 hover:text-sortio-primary transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="mt-8 flex justify-between">
              <Link
                to={`/articles/${category}`}
                className="apple-panel-button inline-flex items-center px-5 py-2.5"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                More in {article.category || 'Articles'}
              </Link>

              <Link
                to="/articles"
                className="apple-panel-button inline-flex items-center px-5 py-2.5"
              >
                All Articles
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 mt-8 lg:mt-0">
            {/* Desktop TOC */}
            {tableOfContents.length > 0 && (
              <div className="hidden lg:block sticky top-24 apple-panel p-6 mb-8">
                <h3 className="text-lg font-medium mb-4 text-apple-100 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  Table of Contents
                </h3>
                <TableOfContents tableOfContents={tableOfContents} />
              </div>
            )}

            {/* Related content */}
            <div className="apple-panel p-6">
              <h3 className="text-lg font-medium mb-4 text-apple-100">Get Sortio</h3>
              <p className="text-apple-300 mb-4">Ready to experience the power of AI-driven file organization?</p>
              <Link to="/download" className="apple-button w-full justify-center py-2.5 mb-4 flex">
                Download Now
              </Link>
              <a href="/#pricing" className="apple-panel-button w-full justify-center py-2.5 flex">
                View Pricing
              </a>
            </div>
          </aside>
        </div>
      </div>
      <ArticleStructuredData article={article} category={category} />
    </div>
  );
};

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export default ArticlePage;
